import type { ColumnDef, Header } from '@tanstack/react-table';
import {
  DataTableColumnResizeHandle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  createAppColumnHelper,
  type DataTableFeatures
} from 'design-system';
import { ChevronDown } from 'lucide-react';

// oxlint-disable-next-line gx-boundaries/no-cross-feature-import -- FactsheetDialogButton is the approved factsheet stop boundary for market widgets.
import { FactsheetDialogButton } from '@/features/factsheet';

import type { SpotHistoryTableColumnType } from '../../types';
import {
  DATA_COLUMN_WIDTH,
  DATE_COLUMN_WIDTH,
  MAX_DATA_COLUMN_WIDTH,
  MIN_DATA_COLUMN_WIDTH,
  RESIZE_KEYBOARD_STEP
} from './constants';
import { getColumnCode, getColumnTitle, getSpotHistoryColumnId } from './spotHistoryColumn';
import type { SpotHistoryDataRow } from './types';

type SpotHistoryColumnOptions = {
  expanded: boolean;
  onResizeEnd: (columnId: string, size: number) => void;
  onToggleDetails: () => void;
};

const columnHelper = createAppColumnHelper<SpotHistoryDataRow>();

export function renderSpotHistoryTableHeader(
  header: Header<DataTableFeatures, SpotHistoryDataRow>,
  { expanded, onResizeEnd, onToggleDetails }: SpotHistoryColumnOptions
) {
  const title =
    typeof header.column.columnDef.header === 'string'
      ? header.column.columnDef.header
      : header.column.id;
  if (!header.column.getCanResize()) return title;

  return (
    <div className='relative flex h-10 w-full min-w-0 items-center'>
      <button
        aria-expanded={expanded}
        className='flex min-w-0 flex-1 items-center justify-end gap-1 text-right focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
        type='button'
        onClick={onToggleDetails}
      >
        <span className='truncate'>{title}</span>
        <ChevronDown
          aria-hidden='true'
          className={`size-4 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
        />
      </button>
      <DataTableColumnResizeHandle
        header={header}
        label={title}
        step={RESIZE_KEYBOARD_STEP}
        onResizeEnd={onResizeEnd}
      />
    </div>
  );
}

export function getSpotHistoryTableColumns(
  columns: SpotHistoryTableColumnType[]
): ColumnDef<DataTableFeatures, SpotHistoryDataRow>[] {
  return columnHelper.columns([
    columnHelper.accessor('formattedDate', {
      id: 'date',
      cell: context => context.getValue(),
      enableResizing: false,
      enableSorting: false,
      header: 'Date',
      maxSize: DATE_COLUMN_WIDTH,
      minSize: DATE_COLUMN_WIDTH,
      size: DATE_COLUMN_WIDTH
    }),
    ...columns.map((column, index) => {
      const columnId = getSpotHistoryColumnId(column, index);
      const title = getColumnTitle(column) || columnId;

      return columnHelper.accessor(row => row.values[columnId] ?? '-', {
        id: columnId,
        cell: context => context.getValue() || '-',
        enableSorting: false,
        header: title,
        maxSize: MAX_DATA_COLUMN_WIDTH,
        meta: { align: 'end' },
        minSize: MIN_DATA_COLUMN_WIDTH,
        size: DATA_COLUMN_WIDTH
      });
    })
  ]);
}

export function renderSpotHistoryHeaderDetails(columns: SpotHistoryTableColumnType[]) {
  return (
    <Table aria-label='Spot history column metadata'>
      <TableHeader>
        <TableRow>
          <TableHead>Market</TableHead>
          <TableHead>Alias</TableHead>
          <TableHead>Code</TableHead>
          <TableHead>Currency</TableHead>
          <TableHead>Units</TableHead>
          <TableHead className='text-right'>Info</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {columns.map((column, index) => {
          const code = getColumnCode(column);
          const columnId = getSpotHistoryColumnId(column, index);

          return (
            <TableRow key={columnId}>
              <TableCell>{getColumnTitle(column) || '-'}</TableCell>
              <TableCell>{column.alias || '-'}</TableCell>
              <TableCell>{code || '-'}</TableCell>
              <TableCell>{column.currency || '-'}</TableCell>
              <TableCell>{column.units || '-'}</TableCell>
              <TableCell className='text-right'>
                {code ? <FactsheetDialogButton code={code} /> : '-'}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
